import { Injectable, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CreateCompanyInvoiceFormGroup } from './create-company-invoice-form-group.interface';
import { CreateCompanyInvoiceLineFormGroup } from './create-company-invoice-line-form-group.interface';

export interface CreateCompanyInvoiceLineDefaults {
  inventoryItemId: string | null;
  quantity: number;
  unitOfMeasureId: number | null;
  unitPrice: number | null;
  taxPercent: number | null;
  description: string | null;
  salesOrderLineId?: string | null;
}

/**
 * Reactive form model for the top-level "Create invoice" modal.
 *
 * Customer is required. Sales-order link is optional. Each line keeps the optional
 * sales-order-line backreference for the linked-mode payload, but the form does not
 * require it.
 */
@Injectable()
export class CreateCompanyInvoiceFormModel {
  private readonly formBuilder = inject(FormBuilder);

  public readonly fieldNames = {
    customerId: 'customerId',
    salesOrderId: 'salesOrderId',
    invoiceDate: 'invoiceDate',
    dueDate: 'dueDate',
    lines: 'lines',
  } as const;

  public readonly form: FormGroup<CreateCompanyInvoiceFormGroup>;

  // Out-of-band store for the salesOrderLineId associated with each line. Stored separately so
  // the form value type stays narrow; reads happen at submit time only.
  private readonly salesOrderLineIdByForm = new WeakMap<
    FormGroup<CreateCompanyInvoiceLineFormGroup>,
    string | null
  >();

  constructor() {
    const formBuilder = this.formBuilder;

    this.form = formBuilder.group<CreateCompanyInvoiceFormGroup>({
      customerId: new FormControl<string | null>(null, { validators: [Validators.required] }),
      salesOrderId: new FormControl<string | null>(null),
      invoiceDate: new FormControl<string | null>(null, { validators: [Validators.required] }),
      dueDate: new FormControl<string | null>(null),
      lines: formBuilder.array<FormGroup<CreateCompanyInvoiceLineFormGroup>>(
        [],
        { validators: [Validators.required] },
      ),
    });
  }

  public get lines(): FormArray<FormGroup<CreateCompanyInvoiceLineFormGroup>> {
    return this.form.controls.lines;
  }

  public addLine(
    defaults: CreateCompanyInvoiceLineDefaults,
  ): FormGroup<CreateCompanyInvoiceLineFormGroup> {
    const lineForm = this.formBuilder.group<CreateCompanyInvoiceLineFormGroup>({
      inventoryItemId: new FormControl<string | null>(defaults.inventoryItemId, {
        validators: [Validators.required],
      }),
      quantity: new FormControl<number | null>(defaults.quantity, {
        validators: [Validators.required, Validators.min(0.0001)],
      }),
      unitOfMeasureId: new FormControl<number | null>(defaults.unitOfMeasureId, {
        validators: [Validators.required],
      }),
      unitPrice: new FormControl<number | null>(defaults.unitPrice, {
        validators: [Validators.min(0)],
      }),
      taxPercent: new FormControl<number | null>(defaults.taxPercent, {
        validators: [Validators.min(0)],
      }),
      description: new FormControl<string | null>(defaults.description),
    });

    this.salesOrderLineIdByForm.set(lineForm, defaults.salesOrderLineId ?? null);
    this.lines.push(lineForm);
    return lineForm;
  }

  public removeLine(index: number): void {
    const lineForm = this.lines.at(index);
    this.salesOrderLineIdByForm.delete(lineForm);
    this.lines.removeAt(index);
  }

  public clearLines(): void {
    while (this.lines.length > 0) {
      this.lines.removeAt(0);
    }
  }

  public getSalesOrderLineId(
    lineForm: FormGroup<CreateCompanyInvoiceLineFormGroup>,
  ): string | null {
    return this.salesOrderLineIdByForm.get(lineForm) ?? null;
  }

  public resetTo(invoiceDate: string, dueDate: string | null): void {
    this.clearLines();
    this.form.controls.customerId.setValue(null);
    this.form.controls.salesOrderId.setValue(null);
    this.form.controls.invoiceDate.setValue(invoiceDate);
    this.form.controls.dueDate.setValue(dueDate);
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }
}
